/**
 * WEB-1994 — МЕСТО ПОД КАРТИНКУ ДО ЗАГРУЗКИ.
 *
 * Задача. Картинка во всю ширину с высотой «по содержимому» (`height: auto`)
 * до загрузки занимает ноль: текст и кнопки под ней стоят выше, а в момент
 * прихода картинки уезжают вниз. Это сдвиг вёрстки (CLS); в воронке он
 * приходится ровно на момент тапа по варианту ответа.
 *
 * Почему именно `aspect-ratio: auto <W> / <H>`, а не просто `aspect-ratio`.
 * Форма с ключевым словом `auto` означает по спецификации: «у замещаемого
 * элемента (картинки) СОБСТВЕННАЯ пропорция важнее указанной». То есть:
 *   · картинки ещё нет → собственной пропорции нет → работает запасная W/H,
 *     и место под картинку зарезервировано;
 *   · картинка пришла → её настоящая пропорция ПЕРЕБИВАЕТ запасную, и вид
 *     ровно тот же, что до этой карточки.
 * Без `auto` запасная пропорция осталась бы навсегда и обрезала/растянула бы
 * все уже опубликованные воронки — это была бы не починка сдвига, а регресс
 * вида. Оговорка честности: если настоящая пропорция расходится с запасной,
 * маленький сдвиг в момент загрузки остаётся — он уйдёт, когда в содержимое
 * блока начнут доезжать НАСТОЯЩИЕ размеры картинки (серверная часть п.1
 * карточки, отдельный объём).
 *
 * Подложки цвета темы под картинкой здесь СОЗНАТЕЛЬНО нет (карточка её
 * предлагала): фон на самой `<img>` виден сквозь прозрачные PNG и после
 * загрузки — у логотипа на цветной странице появился бы светлый прямоугольник,
 * то есть тот самый регресс вида, ради отсутствия которого взят `auto`.
 */
import { useEffect, useState, type CSSProperties } from 'react';

/**
 * Запасная пропорция одна на весь рендер: 4:3 — ближайшее к типовой
 * горизонтальной фотографии воронки. Числа тут нарочно одни: вторая правда
 * («у карточки 16:9, у сравнения 3:4») развела бы рендер и сторож.
 */
export const FALLBACK_IMAGE_RATIO = '4 / 3';

/** Высота считается ЗАДАННОЙ, если это не «по содержимому». */
function hasFixedHeight(style: CSSProperties): boolean {
  const h = style.height;
  if (h === undefined || h === null) return false;
  return !(typeof h === 'string' && h.trim().toLowerCase() === 'auto');
}

/**
 * Достроить стилю картинки резерв места. Ничего не делает, когда место уже
 * зарезервировано автором блока (задана высота кадра или своя пропорция) —
 * авторская настройка сильнее запасной.
 */
export function reserveImageSpace(
  style: CSSProperties,
  ratio: string = FALLBACK_IMAGE_RATIO,
): CSSProperties {
  if (style.aspectRatio !== undefined || hasFixedHeight(style)) return style;
  return { ...style, aspectRatio: `auto ${ratio}` };
}

/**
 * Починка ревизии — «резерв места» верен только пока картинка МОЖЕТ прийти.
 * Сломанный/мёртвый адрес никогда не пришлёт собственную пропорцию, которая
 * по замыслу `auto <W>/<H>` обязана перебить запасную (см. докблок файла) —
 * без снятия резерва он остаётся навсегда, и вместо контента показывается
 * пустая коробка во всю ширину.
 *
 * Единый приём для ВСЕХ мест, где применяется `reserveImageSpace` у `<img>`
 * без своей заглушки на ошибку (карточка предпочтения, обложка карточки,
 * до/после) — тот же исход, что и у основного блока `image`
 * (`PublicBlockRenderer.tsx`), но без четырёх копий одного и того же
 * состояния `imageLoadError`/`useEffect` по каждому месту.
 */
export function useImageSpaceReserve(
  style: CSSProperties,
  imageUrl: string | undefined,
  ratio: string = FALLBACK_IMAGE_RATIO,
): { style: CSSProperties; onError: () => void } {
  const [hasError, setHasError] = useState(false);
  // Автор поменял ссылку — прежняя ошибка к новой картинке отношения не
  // имеет; резерв снова нужен, пока новая картинка не подтвердила обратное.
  useEffect(() => {
    setHasError(false);
  }, [imageUrl]);
  return {
    style: hasError ? style : reserveImageSpace(style, ratio),
    onError: () => setHasError(true),
  };
}
